import type { ResolvedTheme, ThemePreference } from '@/stores/useAppPreferencesStore'
import { resolveThemePreference } from '@/stores/useAppPreferencesStore'
import { useEffect, useState } from 'react'

const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)'

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light'
  }
  return window.matchMedia(DARK_SCHEME_QUERY).matches ? 'dark' : 'light'
}

export function useResolvedTheme(themePreference: ThemePreference): ResolvedTheme {
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveThemePreference(themePreference, getSystemTheme()),
  )

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setResolvedTheme(resolveThemePreference(themePreference, 'light'))
      return
    }

    const mediaQuery = window.matchMedia(DARK_SCHEME_QUERY)
    const updateTheme = () =>
      setResolvedTheme(
        resolveThemePreference(themePreference, mediaQuery.matches ? 'dark' : 'light'),
      )

    updateTheme()

    if (themePreference !== 'system') {
      return
    }

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateTheme)
      return () => mediaQuery.removeEventListener('change', updateTheme)
    }

    mediaQuery.addListener(updateTheme)
    return () => mediaQuery.removeListener(updateTheme)
  }, [themePreference])

  return resolvedTheme
}
